import {
	LOGIN_STEPS,
	OTP_METHODS,
	personMutatorKeyMatcher,
	useLoginLess,
} from '@/data/Content/Login';
import { usePasswordExpiredRedirect } from '@/data/Content/hooks/usePasswordExpiredRedirect';
import {
	Box,
	Divider,
	IconButton,
	List,
	ListItem,
	Modal,
	Paper,
	Stack,
	Typography,
} from '@mui/material';
import { useSWRConfig } from 'swr';
import CloseIcon from '@mui/icons-material/Close';
import {
	ModalLoginContentSx,
	ModalLoginFrameSx,
	ModalLoginFullContentSx,
	ModalLoginHeaderIconCloseFrameSx,
	ModalLoginHeaderIconCloseSx,
	ModalLoginRegisterContentSx,
	ModalLoginRegisterTextSx,
	ModalLoginRegisterTitleSx,
	ModalLoginTitleSx,
} from '@/components/content/Cart/parts/ModalLogin/styles/main';
import { DpButton } from '@/components/blocks/DpLibrary/DpButton';
import { useSuccessLoginNotify } from '@/utils/useSuccessLoginNotify';
import { useReplaceAuthRoute } from 'integration/hooks/useReplaceAuthRoute';
import { SingleLoginLessForm } from '@/components/blocks/SingleLoginLessForm';
import { SingleLoginLessTitle } from '@/components/blocks/SingleLoginLessTitle';
import { useRef } from 'react';
import { RegistrationOrigin } from '@/data/Content/Registration';
import { ZIndex } from '@/utils/zIndex';

interface ModalLoginProps {
	origin?: RegistrationOrigin;
	type?: 'login' | 'wishlist' | 'dp-fan';
	onClose: () => void;
	onGoRegister: () => void;
	onAuthSuccess?: () => void;
}

const computeTitle = (type: ModalLoginProps['type']) => {
	switch (type) {
		case 'login':
			return '¿CÓMO QUIERES CONTINUAR TU COMPRA?';
		case 'wishlist':
			return 'Crea una lista con tus productos favoritos';
		default:
			return 'INICIA SESIÓN O REGÍSTRATE PARA CONTINUAR';
	}
};

export const ModalLogin: React.FC<ModalLoginProps> = ({
	type = 'login',
	origin,
	onClose,
	onGoRegister,
	onAuthSuccess,
}) => {
	const { mutate } = useSWRConfig();
	const { showSuccessAuthLogin } = useSuccessLoginNotify();
	const { onReplaceAuthRoute } = useReplaceAuthRoute({ shouldCallToModal: true, origin });

	const {
		stateLogin,
		baseSettings,
		singleSettingsLoading,
		nextRouteLoading,
		onChangeLogonId,
		onBlurLogonId,
		onSubmitInitialLogonId,
		onChangeOtpMethod,
		onSubmitOtpMethod,
		onChangeOtp,
		onToggleLoginPassword,
		onChangePassword,
		onSubmitLogin,
		onSubmitGoogleLogin,
		onToggleSelectOtpMethod,
		onToggleBackStep,
		loginBase: { passwordExpired, setPasswordExpired },
	} = useLoginLess();

	usePasswordExpiredRedirect(passwordExpired, setPasswordExpired, onClose);

	const currentExecuting = useRef(false);

	const handleSubmit = async () => {
		if (currentExecuting.current) {
			return;
		}

		currentExecuting.current = true;

		const resp = await onSubmitLogin({});

		if ('userId' in (resp ?? {})) {
			!onAuthSuccess && (await onReplaceAuthRoute());
			await mutate(personMutatorKeyMatcher(''), undefined);
			await showSuccessAuthLogin();
			!onAuthSuccess && (await onClose());
			await onAuthSuccess?.();
		}
		currentExecuting.current = false;
	};

	const handleSubmitOtpMethod = async () => {
		if (currentExecuting.current) {
			return;
		}

		currentExecuting.current = true;

		const resp = await onSubmitOtpMethod({});

		if ('userId' in (resp ?? {})) {
			!onAuthSuccess && (await onReplaceAuthRoute());
			await mutate(personMutatorKeyMatcher(''), undefined);
			await showSuccessAuthLogin();
			!onAuthSuccess && (await onClose());
			await onAuthSuccess?.();
		}
		currentExecuting.current = false;
	};

	const handleGoogleLogin = async (idToken: string) => {
		if (currentExecuting.current) {
			return;
		}

		currentExecuting.current = true;

		const resp = await onSubmitGoogleLogin(idToken);

		if ('userId' in (resp ?? {})) {
			!onAuthSuccess && (await onReplaceAuthRoute());
			await mutate(personMutatorKeyMatcher(''), undefined);
			await showSuccessAuthLogin();
			!onAuthSuccess && (await onClose());
			await onAuthSuccess?.();
		}
		currentExecuting.current = false;
	};
	const isFullContent = stateLogin.step === LOGIN_STEPS.INITIAL_LOGON_ID;

	return (
		<Modal
			open
			slotProps={{
				root: {
					sx: { zIndex: ZIndex.HIGH },
				} as any,
			}}
			onClose={onClose}
		>
			<Paper sx={ModalLoginFrameSx}>
				<Stack direction="row" justifyContent="flex-end" width="100%">
					<IconButton onClick={onClose} sx={ModalLoginHeaderIconCloseFrameSx}>
						<CloseIcon sx={ModalLoginHeaderIconCloseSx} />
					</IconButton>
				</Stack>
				{isFullContent ? (
					<Typography sx={ModalLoginTitleSx}>{computeTitle(type)}</Typography>
				) : null}
				<Box sx={!isFullContent ? ModalLoginFullContentSx : ModalLoginContentSx}>
					<Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
						<SingleLoginLessTitle
							sx={ModalLoginRegisterTitleSx}
							step={stateLogin.step}
							otpMethod={stateLogin.otpMethod}
							showPreviousMethodHint={stateLogin.showPreviousMethodHint}
							maskedPhone={stateLogin.maskedPhone}
							maskedEmail={stateLogin.maskedEmail}
							showInDialog
							origin={origin}
							onToggleBackStep={onToggleBackStep}
						/>
						<SingleLoginLessForm
							stateLogin={stateLogin}
							baseSettings={baseSettings}
							disabled={singleSettingsLoading || nextRouteLoading}
							showInDialog={['quick_cart', 'cart'].includes(origin ?? '')}
							onChangeLogonId={onChangeLogonId}
							onBlurLogonId={onBlurLogonId}
							onSubmitInitialLogonId={onSubmitInitialLogonId}
							onChangeOtpMethod={onChangeOtpMethod}
							onSubmitOtpMethod={handleSubmitOtpMethod}
							onChangeOtp={onChangeOtp}
							onChangePassword={onChangePassword}
							onSubmitLogin={handleSubmit}
							onToggleLoginPassword={onToggleLoginPassword}
							onToggleSelectOtpMethod={onToggleSelectOtpMethod}
							onGoogleLogin={handleGoogleLogin}
						/>
					</Box>
					{stateLogin.step === LOGIN_STEPS.INITIAL_LOGON_ID ? (
						<>
							<Divider orientation="vertical" />
							<Stack sx={ModalLoginRegisterContentSx}>
								<Box>
									<Typography sx={{ ...ModalLoginRegisterTitleSx, mb: '14px' }}>
										{'CREAR CUENTA'}
									</Typography>
									<Typography sx={ModalLoginRegisterTextSx}>
										{
											'Regístrate super rápido y recibe todos los beneficios de ser un usuario dportenis.'
										}
									</Typography>

									<List sx={{ ...ModalLoginRegisterTextSx, listStyleType: 'disc', pl: 2 }}>
										<ListItem sx={{ display: 'list-item', p: 0 }}>
											<Typography variant="body1">
												{'Promociones exclusivas y acumula puntos al comprar.'}
											</Typography>
										</ListItem>
										<ListItem sx={{ display: 'list-item', p: 0 }}>
											<Typography variant="body1">
												{'Ve el seguimiento a tu pedido en todo momento.'}
											</Typography>
										</ListItem>
										<ListItem sx={{ display: 'list-item', p: 0 }}>
											<Typography variant="body1">
												{'Realiza devolución o cambio, de manera rápida y fácil.'}
											</Typography>
										</ListItem>
									</List>
								</Box>
								<DpButton
									sx={{
										justifySelf: 'flex-end',
										'&:hover': {
											backgroundColor: '#E8E8E8',
										},
									}}
									variant="unfocused"
									onClick={onGoRegister}
								>
									{'Me quiero registrar'}
								</DpButton>
							</Stack>
						</>
					) : null}
				</Box>
			</Paper>
		</Modal>
	);
};
